import { Navigate, useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetCertificationQuery } from "@/redux/service";
import ModuleSkeleton from "@/features/certifications/module/components/ModuleSkeleton";
import { useCertificationProgress } from "@/domains/certifications/hooks/useCertificationProgress";
import {
  buildModuleOverviews,
  findModuleWeek,
} from "@/domains/certifications/progress";
import { certificationPaths } from "@/domains/certifications/routes";
import {
  isNotEnrolledError,
  isRemovedFromCertificationError,
} from "@/domains/certifications/utils";
import CertificationModule from "@/features/certifications/module";
import {
  ENROLLMENT_STATES,
  MODULE_STATUSES,
} from "@/domains/certifications/constants";

const CertificationModulePage = () => {
  const { slug, moduleId: moduleIdParam } = useParams();
  const moduleId = Number(moduleIdParam);
  const {
    data: certification,
    error,
    isError,
    isFetching,
  } = useGetCertificationQuery(slug ?? skipToken);
  const { data: progress, isError: isProgressError } =
    useCertificationProgress(certification);
  const isEnrolled =
    certification?.enrollment?.state === ENROLLMENT_STATES.ACTIVE;

  if (
    slug &&
    (isNotEnrolledError(error) || isRemovedFromCertificationError(error))
  ) {
    return <Navigate to={certificationPaths.details(slug)} replace />;
  }

  if (!slug || isError || (isProgressError && !progress)) {
    return <Navigate to="/" replace />;
  }

  if (certification && !isEnrolled && !isFetching) {
    return <Navigate to={certificationPaths.details(slug)} replace />;
  }

  if (!certification || !progress) {
    return <ModuleSkeleton />;
  }

  const moduleWeek = findModuleWeek(
    buildModuleOverviews(certification, progress, progress.selectedTrack),
    moduleId,
  );
  const track = certification.tracks.find(
    (item) => item.id === progress.selectedTrack,
  );
  const moduleProgress = progress.modules.find(
    (module) => module.id === moduleId,
  );

  if (
    !moduleWeek ||
    moduleWeek.week.status === MODULE_STATUSES.LOCKED ||
    !track ||
    !moduleProgress
  ) {
    return <Navigate to={certificationPaths.overview(slug)} replace />;
  }

  return (
    <CertificationModule
      key={moduleId}
      certification={certification}
      track={track}
      moduleWeek={moduleWeek}
      moduleProgress={moduleProgress}
    />
  );
};

export default CertificationModulePage;
